// StorePreview.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from "mobx-react";
import { useStore } from "../../hooks/storeContext.js";
import { Spin } from 'antd';
import StoreContent from '../StoreComponents/StoreContent/index.js';
import { CustomCloseButton } from "./styled.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { LoadingOutlined } from '@ant-design/icons';
import { StyledButton } from "../../components/Button/styled.js";
import { ROUTE_PATH } from '../../constants/routePath.js';
import ecommerceService from "../../services/ecommerce/service.js";

const StorePreview = observer(({ onClose, isVisible }) => {
    const navigate = useNavigate();
    const { authStore } = useStore();
    const { storeName } = useParams();
    const [userData, setUserData] = useState(null);
    const [theme, setTheme] = useState(null);
    const [items, setItems] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [commerceProducts, setCommerceProducts] = useState([]);
    const [isShopDisplayed, setIsShopDisplayed] = useState(false);

    const isPreview = !storeName;

    useEffect(() => {
        if (isVisible) {
            setIsLoaded(false);
            const fetchData = async () => {
                try {
                    if (isPreview) {
                        await authStore.fetchUserDetailsMyStore();
                        await authStore.fetchProducts();
                        await authStore.fetchHeadlines();
                        await authStore.fetchTheme(authStore.me?.userName);
                    } else {
                        await authStore.getStoreData(storeName);
                        await authStore.fetchTheme(storeName);
                    }

                    // Fetch commerce products and shop settings
                    const [fetchedCommerceProducts] = await Promise.all([
                        ecommerceService.getCommerceProducts(authStore.me?.userName),
                    ]);

                    setCommerceProducts(fetchedCommerceProducts);
                    setIsShopDisplayed(authStore.me?.isShopDisplayed);

                    // Prepare items (products and headlines)
                    const products = authStore.products
                        .filter(product => product.isPublished)
                        .map(product => {
                            const baseItem = {
                                id: product._id,
                                text: product.title,
                                thumbnail: product.thumbnail,
                                subtitle: product.subtitle,
                                price: product.price,
                                priceDisplay:
                                    product.price === 0
                                        ? 'Free'
                                        : typeof product.price === 'number'
                                            ? `$${product.price.toFixed(2)}`
                                            : '',
                                buttonName: product.buttonName,
                                order: product.order,
                                url: product.url,         // For general link or fallback
                                type: product.type,
                                cardType: product.cardType || 'button',
                                checkoutUrl: product.checkoutUrl,
                                videos: product.videos,
                                useCarousel: product.useCarousel,
                                creatorImages: product.creatorImages || [],
                            };

                            // --- Spotify block ---
                            if (product.type === 'spotify') {
                                // If your backend returns these fields on the product directly:
                                baseItem.spotifyUrl = product.spotifyData.spotifyUrl;
                                baseItem.spotifyType = product.spotifyData.spotifyType;   // 'artist' or 'album'
                                baseItem.spotifyId = product.spotifyData.spotifyId;
                                baseItem.displayOption = product.spotifyData.displayOption;
                                baseItem.artistName = product.spotifyData.artistName;     // If album
                                baseItem.artistImage = product.spotifyData.artistImage;   // If artist
                                baseItem.albumImage = product.spotifyData.albumImage;     // If album
                                baseItem.albumName = product.spotifyData.albumName;       // If album
                                baseItem.albumReleaseDate = product.spotifyData.albumReleaseDate; // If album
                                // Any other fields you need
                            } else if (product.type === 'apple') {
                                baseItem.appleUrl = product.appleData.appleUrl;
                                baseItem.appleType = product.appleData.appleType;  // 'artist', 'album', 'playlist', etc.
                                baseItem.appleId = product.appleData.appleId;
                                baseItem.displayOption = product.appleData.displayOption;
                                baseItem.artistName = product.appleData.artistName;
                                baseItem.albumName = product.appleData.albumName;
                                // Add more fields your Apple embed needs
                            } else if (product.type === 'tip') {
                                baseItem.checkoutImage = product.checkoutImage;
                                baseItem.descriptionTitle = product.descriptionTitle;
                                baseItem.descriptionBody = product.descriptionBody;
                                baseItem.tipOption1Amount = product.tipOption1Amount;
                                baseItem.tipOption2Amount = product.tipOption2Amount;
                                baseItem.tipOption3Amount = product.tipOption3Amount;
                                baseItem.allowCustomAmount = product.allowCustomAmount;
                            } else if (product.type === 'image') {
                                baseItem.creatorImages = product.creatorImages;
                                baseItem.creatorImagesV2 = product.creatorImagesV2 || [];
                                baseItem.subtitle = product.descriptionBody;  
                            } else if (product.type === 'engagement') {
                                baseItem.engagementData = product.engagementData;
                            }

                            return baseItem;
                        });

                    const headlines = authStore.headlineSections.map(headline => ({
                        id: headline._id,
                        text: headline.headline,
                        order: headline.order,
                        type: 'headline'
                    }));

                    const combinedItems = [...products, ...headlines].sort((a, b) => a.order - b.order);

                    setItems(combinedItems);
                    setTheme(authStore.theme);

                    setUserData({
                        user: {
                            fullName: authStore.me?.fullName || '',
                            avatar: authStore.me?.avatar || '',
                            socialLinks: authStore.me?.socialLinks || {},
                            bio: authStore.me?.bio || '',
                            displayName: authStore.me?.displayName || '',
                            isVerified: authStore.me?.isVerified || false,
                            isShopDisplayed: authStore.me?.isShopDisplayed || false,
                        },
                        headlineSections: authStore.headlineSections,
                        products: authStore.products.filter(product => product.isPublished)
                    });

                    setIsLoaded(true);
                } catch (error) {
                    console.error("Error fetching data:", error);
                    setIsLoaded(true);
                }
            };

            fetchData();
        }
    }, [isVisible, storeName, authStore, isPreview]);

    const handleClose = () => {
        onClose();
    };

    const handleAddItems = () => {
        onClose();
        navigate(ROUTE_PATH.MYSTORE);
    };

    return (
        <>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                overflow: 'auto',
                backgroundColor: theme?.background_color || '#f5f5f5',
                transition: 'transform 0.3s ease-out',
                transform: `translateY(${isVisible ? '0' : '100%'})`,
            }}>
                {isLoaded ? (
                    <div style={{
                        position: 'relative',
                        minHeight: '100%',
                        backgroundColor: theme?.background_color || '#f5f5f5'
                    }}>
                        <StoreContent
                            userData={userData}
                            theme={theme}
                            items={items}
                            storeName={isPreview ? authStore.me?.userName : storeName}
                            commerceProducts={commerceProducts}
                            isShopDisplayed={isShopDisplayed}
                        />
                        {items.length === 0 && (
                            <div style={{
                                position: 'relative',
                                marginTop: theme?.image_style === 'Hero' ? '50px' : '50px',
                                textAlign: 'center',
                                width: '50%',
                                maxWidth: '700px',
                                margin: '0 auto',
                                zIndex: 1001
                            }}>
                                <p>No items in your store yet.</p>
                                <StyledButton
                                    onClick={handleAddItems}
                                    style={{
                                        padding: '15px 40px',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        backgroundColor: theme?.buttonColor || '#007bff',
                                        color: theme?.buttonTextColor || 'white',
                                        border: 'none',
                                        marginTop: '10px'
                                    }}
                                >
                                    Add Links/Products
                                </StyledButton>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '77vh' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 77 }} spin />} />
                    </div>
                )}
            </div>
            {isPreview && isVisible && (
                <CustomCloseButton onClick={handleClose}>
                    <FontAwesomeIcon icon={faClose} size="2x" />
                </CustomCloseButton>
            )}
        </>
    );
});

export default StorePreview;